import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  doc,
  getDoc,
  getDocs,
  collection,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../firebase/firebase";

import TestHeader from "../../components/Test/TestHeader";
import TestFooter from "../../components/Test/TestFooter";
import QuestionColumn from "../../components/Test/QuestionColumn";
import ContextColumn from "../../components/Test/ContextColumn";
import ColumnDivider from "../../components/Test/ColumnDivider";
import ReviewNavigator from "../../components/Test/ReviewNavigator";
import DesmosCalculatorWindow from "../../components/Test/DesmosCalculatorWindow";
import ReferenceWindow from "../../components/Test/ReferenceWindow";
import EditQuestion from "../../components/Tutor/EditQuestion";

import useResizableColumns from "../../hooks/useResizableColumns";

import { updateQuestion } from "../../utils/tutor/updateQuestion";

import "../../styles/testLayout.css";

const TutorQuestionPage = () => {
  const navigate = useNavigate();
  const { assignmentId } = useParams();
  const { state = {} } = useLocation();
  const sourceType = state.sourceType; // "packet" | "diagnostic"

  const [packet, setPacket] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [showCalculator, setShowCalculator] = useState(false);
  const [showReference, setShowReference] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const isReviewPage = currentIndex === questions.length;
  const currentQuestion = questions[currentIndex];

  const { containerRef, leftWidth, rightWidth, startResize } =
    useResizableColumns({ initial: 50, min: 25, max: 75 });

  /* ───────────────────────────────── DATA LOAD ───────────────────────────── */
  useEffect(() => {
    const load = async () => {
      if (!assignmentId || !sourceType) {
        navigate("/tutor/assignments");
        return;
      }

      setLoading(true);
      try {
        /* ── 1. fetch packet / diagnostic meta ─────────────────────────── */
        const col = sourceType === "packet" ? "packets" : "diagnostics";
        const snap = await getDoc(doc(db, col, assignmentId));
        if (!snap.exists()) throw new Error("doc not found");

        const meta = { id: snap.id, ...snap.data() };
        const ids = meta.questionIds || [];
        setPacket(meta);

        if (ids.length === 0) {
          setQuestions([]);
          return;
        }

        /* ── 2. fetch questions in chunks (Firestore 'in' limit) ───────── */
        const chunks = [];
        for (let i = 0; i < ids.length; i += 10) chunks.push(ids.slice(i, i + 10));

        const all = [];
        for (const chunk of chunks) {
          const q = query(collection(db, "questions"), where("__name__", "in", chunk));
          (await getDocs(q)).forEach((d) => all.push({ id: d.id, ...d.data() }));
        }

        /* ── 3. normalize + preserve order ─────────────────────────────── */
        const map = Object.fromEntries(all.map((q) => [q.id, q]));
        const ordered = ids
          .map((id) => map[id])
          .filter(Boolean)
          .map((q) => ({
            ...q,
            // 🗝️ map fields so QuestionColumn & ContextColumn can render
            questionText: q.questionText ?? q.prompt ?? q.text ?? q.question ?? "",
            prompt: q.prompt ?? q.text ?? q.question ?? "",
            choices: q.choices ?? q.answerChoices ?? {},
            correctAnswer: q.correctAnswer ?? q.answer?.correct ?? null,
            passage: q.passage ?? q.context ?? null,
          }));

        setQuestions(ordered);
      } catch (err) {
        console.error("load failed", err);
        navigate("/tutor/assignments");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [assignmentId, sourceType, navigate]);

  if (loading) return <div>Loading…</div>;
  if (!packet) return null;

  const handleExit = () => navigate("/tutor/assignments");

  const handleSaveEdit = async (updatedQ) => {
    try {
      console.log("💾 saving question to Firestore", updatedQ);
      await updateQuestion(updatedQ);
      setQuestions((prev) =>
        prev.map((q) => (q.id === updatedQ.id ? updatedQ : q))
      );
      setEditOpen(false);
    } catch (err) {
      console.error("❌ failed to save question", err);
      alert("Failed to save question. Check console.");
    }
  };

  return (
    <div
      ref={containerRef}
      className="test-page"
      style={{ "--left-col": `${leftWidth}%`, "--right-col": `${rightWidth}%` }}
    >
      <TestHeader
        title={packet.name || packet.title}
        section={packet.section}
        timeRemaining={null}
        onExit={handleExit}
        onToggleCalculator={() => setShowCalculator((p) => !p)}
        onToggleReference={() => setShowReference((p) => !p)}
        isCalculatorOpen={showCalculator}
        isReferenceOpen={showReference}
        isTutor
        onEditQuestion={() => setEditOpen(true)}
      />

      {showReference && (
        <ReferenceWindow onClose={() => setShowReference(false)} />
      )}
      {showCalculator && (
        <DesmosCalculatorWindow onClose={() => setShowCalculator(false)} />
      )}

      {isReviewPage ? (
        <div className="test-main" style={{ display: "flex", justifyContent: "center" }}>
          <ReviewNavigator
            currentIndex={currentIndex}
            answers={questions.map((q) => q.correctAnswer ?? null)}
            marked={questions.map(() => false)}
            onJump={setCurrentIndex}
          />
        </div>
      ) : (
        <div className="test-main">
          <ContextColumn
            passage={currentQuestion?.passage}
            context={currentQuestion?.context}
            imageUrl={currentQuestion?.imageUrl}
          />

          <ColumnDivider onMouseDown={startResize} />

          <QuestionColumn
            question={currentQuestion}
            questionIndex={currentIndex}
            totalQuestions={questions.length}
            selectedAnswer={currentQuestion?.correctAnswer}
            isMarked={false}
            isReadOnly
            showCorrectness
            showExplanation
          />
        </div>
      )}

      <TestFooter
        currentIndex={currentIndex}
        totalQuestions={questions.length}
        setCurrentIndex={setCurrentIndex}
        answers={questions.map((q) => q.correctAnswer ?? null)}
        marked={questions.map(() => false)}
        isReviewPage={isReviewPage}
        isReadOnly
        onExit={handleExit}
      />

      <EditQuestion
        open={editOpen}
        question={currentQuestion}
        onClose={() => setEditOpen(false)}
        onSave={handleSaveEdit}
      />
    </div>
  );
};

export default TutorQuestionPage;
